"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { buildErrorMessage } from "@nimble/react-requests/helpers/error-message";
import {
  ActionTypeEnum,
  MatchActorEnum,
} from "@nimble/recommendation-client/types";
import * as React from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import {
  acceptMatchFormSchema,
  AcceptMatchFormValues,
} from "@/app/(discover)/candidate/invites/components/accept-match-modal/accept-match-form-schema";
import { AcceptMatchFormView } from "@/app/(discover)/candidate/invites/components/accept-match-modal/accept-match-view";
import useInvitesInvalidQueries from "@/app/(discover)/candidate/invites/hooks/use-invites-invalid-queries";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { useTracking } from "@/hooks/use-tracking";
import { createAction } from "@/lib/api-clients/recommendation-api-client";
import { neverthrowize } from "@/lib/data/call-server-api";
import { MatchAcceptedSource } from "@/lib/posthog/track";
import { User } from "@/lib/types/auth";

function getMessageData(message?: string) {
  return message
    ? {
        message: message,
        sender_type: MatchActorEnum.CANDIDATE,
      }
    : null;
}

export async function submitForm({
  data: data,
  form,
  schoolNcesId,
  user,
  onSubmitSuccess,
  submitRequest = createAction,
}: {
  data: AcceptMatchFormValues;
  form: UseFormReturn<AcceptMatchFormValues>;
  user: User;
  schoolNcesId: string;
  onSubmitSuccess: () => void;
  submitRequest?: typeof createAction;
}) {
  const result = neverthrowize([
    submitRequest(user.id, {
      school_nces_id: schoolNcesId,
      action: ActionTypeEnum.CANDIDATE_ACCEPT,
      reason: null,
      explanation: null,
      message_data: getMessageData(data.message),
      notification_data: {
        candidate_email: user.email,
        candidate_first_name: user.first_name,
        candidate_last_name: user.last_name,
      },
    }),
  ]);

  await result.match(onSubmitSuccess, (error: Error) => {
    form.setError("root", {
      message: buildErrorMessage(error),
    });
  });
}

export function AcceptMatchFormContainerInner({
  onCancel,
  onSubmitSuccess,
  schoolNcesId,
  schoolName,
  source,
  user,
}: {
  onCancel: () => void;
  onSubmitSuccess: () => void;
  schoolNcesId: string;
  schoolName: string;
  source: MatchAcceptedSource;
  user: User;
}) {
  const { invalidate } = useInvitesInvalidQueries(user);
  const { trackMatchAccepted } = useTracking();
  const form = useForm<AcceptMatchFormValues>({
    resolver: zodResolver(acceptMatchFormSchema),
    defaultValues: {
      message: undefined,
    },
  });

  const handleSubmitSuccess = () => {
    invalidate();
    trackMatchAccepted({
      schoolNcesId,
      schoolName,
      source,
    });
    onSubmitSuccess();
  };

  return (
    <AcceptMatchFormView
      form={form}
      onCancel={onCancel}
      onSubmit={form.handleSubmit((data) =>
        submitForm({
          data,
          schoolNcesId,
          user,
          form,
          onSubmitSuccess: handleSubmitSuccess,
        }),
      )}
    />
  );
}

export const AcceptMatchFormContainer = withAuthGate(
  AcceptMatchFormContainerInner,
  {},
);
